// File upload API service
import { resolveDefaultSiteId } from '../utils/constants'
import { apiRequest } from '../utils/requests'
import { useAuthStore } from '@/stores/auth'
import type { ApiResponse } from '@/types'
import type { DocumentUploadResponse } from '@/types/document'

type UploadResponseData = {
  type?: string
  url?: string
  s3Key?: string
  filename?: string
  originalFilename?: string
  size?: number
  mimetype?: string
  transcription?: string
  uploadedToS3?: boolean
  presignedUrl?: string
}

const legacyUploadViaProxy = (
  formData: FormData,
  onProgress?: (progress: number) => void
): Promise<ApiResponse<UploadResponseData>> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const token =
      useAuthStore.getState().agentToken || useAuthStore.getState().adminToken

    if (onProgress) {
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable) {
          const progress = (e.loaded / e.total) * 100
          onProgress(progress)
        }
      })
    }

    xhr.addEventListener('load', () => {
      try {
        const response = JSON.parse(xhr.responseText)
        if (xhr.status >= 200 && xhr.status < 300) {
          const payload =
            response && typeof response === 'object'
              ? response.data && typeof response.data === 'object'
                ? response.data
                : response
              : {}
          const normalized: UploadResponseData = {
            type: payload.type,
            url: payload.url,
            s3Key: payload.s3Key,
            filename: payload.filename,
            originalFilename: payload.originalFilename,
            size: payload.size,
            mimetype: payload.mimetype,
            transcription: payload.transcription,
            uploadedToS3: payload.uploadedToS3,
            presignedUrl: payload.presignedUrl,
          }
          resolve({
            status: xhr.status,
            success: true,
            data: normalized,
            error: undefined,
          })
        } else {
          resolve({
            status: xhr.status || 0,
            success: false,
            data: undefined,
            error: response.error || 'Upload failed',
          })
        }
      } catch (error) {
        reject(new Error('Failed to parse upload response'))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'))
    })

    try {
      resolveDefaultSiteId()
    } catch (error) {
      reject(
        error instanceof Error
          ? error
          : new Error(
              'Site ID is not defined and DEFAULT_SITE_ID is not set. Cannot perform file upload.'
            )
      )
      return
    }

    xhr.open('POST', `/api/upload`)

    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    }
    xhr.send(formData)
  })
}

const sanitizeFilename = (
  originalName: string
): { base: string; ext: string } => {
  const trimmed = (originalName || '').trim()
  if (!trimmed) {
    return { base: 'upload', ext: '' }
  }

  const lastDot = trimmed.lastIndexOf('.')
  const hasExt = lastDot > 0 && lastDot < trimmed.length - 1
  const ext = hasExt ? trimmed.slice(lastDot).toLowerCase() : ''
  const basename = hasExt ? trimmed.slice(0, lastDot) : trimmed

  const sanitizedBase = basename.replace(/[^a-zA-Z0-9._-]/g, '_').trim()
  return {
    base: sanitizedBase.length > 0 ? sanitizedBase : 'upload',
    ext,
  }
}

// Generate a key without site prefix - the backend presign endpoint
// prepends the correct `sites/{siteId}/` from request context.
const generateS3Key = (fileName: string): string => {
  const { base, ext } = sanitizeFilename(fileName)
  const uniqueSuffix = `${Date.now()}-${Math.round(Math.random() * 1e9)}`
  return `uploads/${base}-${uniqueSuffix}${ext}`
}

export const uploadAPI = {
  // Generic file upload
  uploadFile: (
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<UploadResponseData>> => {
    const fileEntry = formData.get('file')
    const hasAdditionalFields = (() => {
      for (const key of formData.keys()) {
        if (key !== 'file') {
          return true
        }
      }
      return false
    })()

    console.log('📤 [Upload] Starting upload:', {
      hasFile: !!fileEntry,
      fileType: fileEntry instanceof File ? fileEntry.type : 'N/A',
      hasAdditionalFields,
      isAudio:
        fileEntry instanceof File ? fileEntry.type.startsWith('audio/') : false,
    })

    if (!(fileEntry instanceof File)) {
      console.log('📤 [Upload] Using legacyUploadViaProxy - not a file')
      return legacyUploadViaProxy(formData, onProgress)
    }

    const isAudioUpload = fileEntry.type.startsWith('audio/')
    if (isAudioUpload || hasAdditionalFields) {
      console.log(
        '📤 [Upload] Using legacyUploadViaProxy - audio or extra fields'
      )
      return legacyUploadViaProxy(formData, onProgress)
    }

    console.log(
      '📤 [Upload] Using uploadFileViaPresignedUrl - direct S3 upload'
    )
    return uploadAPI.uploadFileViaPresignedUrl(fileEntry, onProgress)
  },

  // Document upload
  uploadDocument: (
    formData: FormData,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<DocumentUploadResponse>> => {
    // For file uploads, we need to handle progress and use different headers
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      if (onProgress) {
        xhr.upload.addEventListener('progress', e => {
          if (e.lengthComputable) {
            const progress = (e.loaded / e.total) * 100
            onProgress(progress)
          }
        })
      }

      xhr.addEventListener('load', () => {
        try {
          const response = JSON.parse(xhr.responseText)
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ success: true, status: xhr.status, data: response })
          } else {
            resolve({
              success: false,
              status: xhr.status,
              error: response.error || 'Upload failed',
            })
          }
        } catch (error) {
          resolve({
            success: false,
            status: 0,
            error: 'Invalid response format',
          })
        }
      })

      xhr.addEventListener('error', () => {
        resolve({ success: false, status: 0, error: 'Network error' })
      })

      const token =
        typeof window !== 'undefined'
          ? useAuthStore.getState().adminToken
          : null
      const url = `/api/be/admin/documents/upload`

      xhr.open('POST', url)
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      }
      // Site scoping now handled via request path; header is no longer required
      xhr.send(formData)
    })
  },

  getPresignedUrl: async (
    s3Key: string,
    options: {
      expiresIn?: number
      operation?: 'get' | 'put'
      contentType?: string
    } = {}
  ): Promise<
    ApiResponse<{
      presignedUrl: string
      expiresIn: number
      s3Key: string
      url?: string | null
      operation?: 'get' | 'put'
      contentType?: string | null
    }>
  > => {
    if (!s3Key || !s3Key.trim()) {
      return {
        success: false,
        status: 400,
        error: 's3Key is required to generate a presigned URL',
      }
    }

    const { expiresIn, operation, contentType } = options

    return apiRequest<{
      presignedUrl: string
      expiresIn: number
      s3Key: string
      url?: string | null
      operation?: 'get' | 'put'
      contentType?: string | null
    }>('/upload/presign', {
      method: 'POST',
      body: JSON.stringify({
        s3Key,
        expiresIn,
        operation,
        contentType,
      }),
    })
  },

  uploadFileViaPresignedUrl: async (
    file: File,
    onProgress?: (progress: number) => void,
    options: {
      s3Key?: string
      presignExpiresIn?: number
      contentType?: string
    } = {}
  ): Promise<ApiResponse<UploadResponseData>> => {
    const customKey =
      typeof options.s3Key === 'string' && options.s3Key.trim().length > 0
        ? options.s3Key.trim()
        : null
    // Backend presign endpoint prepends the correct site prefix.
    const s3Key = customKey ?? generateS3Key(file.name)
    const uploadContentType =
      typeof options.contentType === 'string' &&
      options.contentType.trim().length > 0
        ? options.contentType
        : file.type || undefined
    const presignResponse = await uploadAPI.getPresignedUrl(s3Key, {
      operation: 'put',
      contentType: uploadContentType,
      expiresIn:
        typeof options.presignExpiresIn === 'number' &&
        Number.isFinite(options.presignExpiresIn)
          ? options.presignExpiresIn
          : 15 * 60,
    })

    if (!presignResponse.success || !presignResponse.data?.presignedUrl) {
      return {
        success: false,
        status: presignResponse.status,
        error:
          presignResponse.error ||
          'Failed to prepare upload destination. Please try again.',
      }
    }

    // Use the s3Key returned by the backend (it enforces the correct site prefix)
    const resolvedS3Key = presignResponse.data.s3Key || s3Key
    const presignedUrl = presignResponse.data.presignedUrl
    const objectUrl =
      (typeof presignResponse.data.url === 'string' &&
      presignResponse.data.url.trim().length > 0
        ? presignResponse.data.url.trim()
        : undefined) ||
      presignedUrl.split('?')[0] ||
      presignedUrl
    const sanitized = sanitizeFilename(file.name)
    const storageFilename =
      resolvedS3Key.split('/').pop() ?? `${sanitized.base}${sanitized.ext}`
    const inferredType = file.type.startsWith('image/')
      ? 'image'
      : file.type.startsWith('audio/')
        ? 'audio'
        : 'file'

    return new Promise<ApiResponse<UploadResponseData>>((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      if (onProgress) {
        xhr.upload.addEventListener('progress', event => {
          if (event.lengthComputable) {
            onProgress((event.loaded / event.total) * 100)
          }
        })
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const finalize = async () => {
            let viewPresignedUrl: string | undefined
            try {
              const viewResponse =
                await uploadAPI.getPresignedUrl(resolvedS3Key)
              if (viewResponse.success && viewResponse.data?.presignedUrl) {
                viewPresignedUrl = viewResponse.data.presignedUrl
              }
            } catch (err) {
              console.warn('Failed to fetch download presigned URL', err)
            }

            resolve({
              success: true,
              status: xhr.status,
              data: {
                url: objectUrl,
                s3Key: resolvedS3Key,
                originalFilename: file.name,
                filename: storageFilename,
                size: file.size,
                mimetype: file.type,
                uploadedToS3: true,
                presignedUrl: viewPresignedUrl,
                type: inferredType,
              },
            })
          }

          finalize().catch(err => {
            console.warn('Failed to finalize upload metadata', err)
            resolve({
              success: true,
              status: xhr.status,
              data: {
                url: objectUrl,
                s3Key: resolvedS3Key,
                originalFilename: file.name,
                filename: storageFilename,
                size: file.size,
                mimetype: file.type,
                uploadedToS3: true,
                presignedUrl: undefined,
                type: inferredType,
              },
            })
          })
        } else {
          resolve({
            success: false,
            status: xhr.status || 0,
            error:
              xhr.statusText ||
              'Failed to upload file to storage. Please try again.',
          })
        }
      })

      xhr.addEventListener('error', () => {
        reject(new Error('Network error while uploading file to storage.'))
      })

      xhr.open('PUT', presignedUrl)
      xhr.setRequestHeader(
        'Content-Type',
        uploadContentType || 'application/octet-stream'
      )
      xhr.send(file)
    })
  },
}
